'use client'

import { forwardRef } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../icon'
import { mtc } from '../../../utils/style-helpers'
import {
  iconLeftStyles,
  iconLeftBgStyles,
  iconRightStyles,
  iconRightBgStyles,
  styles,
} from './styles'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string
  error?: boolean
  iconLeft?: IconDefinition
  iconLeftClassName?: string
  iconRight?: IconDefinition
  iconRightClassName?: string
  onIconRightClick?: () => void
  inputClassName?: string
  placeholder?: string
  errorTestId?: string
  /** Accepts basic input types */
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'hidden'
    | 'tel'
    | 'search'
    | 'url'
    | 'number'
}

/** An input for basic text based input types with no extra controls */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      error,
      iconLeft,
      iconLeftClassName,
      iconRight,
      iconRightClassName,
      onIconRightClick,
      inputClassName,
      placeholder,
      type = 'text',
      errorTestId,
      ...props
    },
    ref
  ) => {
    const hasLeftIcon = Boolean(iconLeft)
    const hasRightIcon = Boolean(iconRight)
    const hasClickableRightIcon = Boolean(iconRight && onIconRightClick)
    return (
      <div className={`w-full relative inline-flex ${className}`}>
        {iconLeft && (
          <Icon
            icon={iconLeft}
            className={mtc(iconLeftStyles(), iconLeftClassName)}
            backgroundClassName={mtc(iconLeftBgStyles())}
            size="sm"
            color="currentColor"
          />
        )}

        <input
          ref={ref}
          type={type}
          placeholder={placeholder}
          className={mtc(
            styles({ error, hasLeftIcon, hasRightIcon }),
            inputClassName
          )}
          {...props}
          data-testid={errorTestId}
        />

        {iconRight && (
          <Icon
            icon={iconRight}
            className={mtc(
              iconRightStyles(),
              hasClickableRightIcon && 'pointer-events-auto',
              iconRightClassName
            )}
            backgroundClassName={mtc(
              iconRightBgStyles(),
              hasClickableRightIcon && 'pointer-events-auto cursor-pointer'
            )}
            size="sm"
            color="currentColor"
            onClick={onIconRightClick}
          />
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
